import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  NativeSelect,
  Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PropTypes from 'prop-types';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import { useGetAiModelsByProviderIdQuery, useGetAiProvidersQuery } from 'services/aiProviderApi';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';

export function ClassificationNodeForm({ id, data, onUpdateData }) {
  const { formatMessage: msg } = useIntl();
  const { providerCode, modelCode, classificationItems = [], outputVar, fileClassification } = data;

  // Fetch providers from API
  const { data: providers = [], isLoading: providersLoading } = useGetAiProvidersQuery();

  const { control, setValue, watch } = useForm({
    defaultValues: {
      providerCode: providerCode || '',
      modelCode: modelCode || '',
      items: classificationItems || [],
      outputVar: outputVar || 'classification',
      fileClassification: fileClassification || false
    }
  });

  // Gestion des items de classification dans un state local
  const [items, setItems] = useState(classificationItems.length > 0 ? classificationItems : ['']);

  const formValues = watch();

  const filteredProviders = providers.filter((p) => p.displayableKey !== '***********');
  const selectedProvider =
    filteredProviders.find((p) => p.code === formValues.providerCode) ?? filteredProviders[0];
  const selectedProviderId = selectedProvider?.id;

  const { data: models = [], isLoading: modelsLoading } = useGetAiModelsByProviderIdQuery(
    selectedProviderId,
    { skip: !selectedProviderId }
  );

  // Sort models: favorites first, then alphabetically by label
  const sortedModels = [...models].sort((a, b) => {
    if (a.favorite && !b.favorite) return -1;
    if (!a.favorite && b.favorite) return 1;

    return a.label.localeCompare(b.label);
  });

  // Initialize provider and model when data loads
  useEffect(() => {
    if (filteredProviders.length > 0 && !formValues.providerCode) {
      setValue('providerCode', filteredProviders[0].code);
    }
  }, [filteredProviders, formValues.providerCode, sortedModels, setValue]);

  // Initialize model when data loads
  useEffect(() => {
    if (sortedModels.length > 0 && (!formValues.modelCode || formValues.modelCode === '-1')) {
      setValue('modelCode', sortedModels[0].code);
    }
    if (formValues.providerCode === '-1') {
      setValue('modelCode', '-1');
    }
  }, [sortedModels, formValues.providerCode, formValues.modelCode, setValue]);

  useEffect(() => {
    onUpdateData?.({
      id,
      data: {
        ...data,
        providerCode: formValues.providerCode === '-1' ? '' : formValues.providerCode,
        modelCode: formValues.modelCode === '-1' ? '' : formValues.modelCode,
        classificationItems: items,
        outputVar: formValues.outputVar,
        fileClassification: formValues.fileClassification
      }
    });
  }, [formValues, items, data, onUpdateData, id]);

  const handleAddItem = () => {
    const newIndex = items.length;
    setItems((prev) => [...prev, '']);
    setTimeout(() => {
      // Utiliser newIndex au lieu de items.length
      document.getElementById('inputClassificationItem' + newIndex)?.focus();
    }, 100);
  };

  const handleItemChange = (index, value) => {
    const updated = [...items];
    updated[index] = value;
    setItems(updated);
  };

  const handleRemoveItem = (index) => {
    if (items.length > 1) {
      const updated = items.filter((_, i) => i !== index);
      setItems(updated);
    }
  };

  return (
    <Stack spacing={1.5} sx={{ p: 1 }}>
      <Controller
        name="providerCode"
        control={control}
        rules={{ required: msg({ id: 'field.provider.required' }) }}
        render={({ field }) => (
          <FormControl fullWidth>
            <InputLabel variant="standard" htmlFor="provider-native-select">
              {msg({ id: 'field.provider.label' })}
            </InputLabel>
            {providersLoading ? (
              <CircularProgress size={24} />
            ) : (
              <NativeSelect
                {...field}
                label={msg({ id: 'field.provider.label' })}
                required
                inputProps={{
                  id: 'provider-native-select',
                  name: 'providerCode'
                }}
              >
                <option value="-1">Sélectionner un fournisseur</option>
                {filteredProviders.map((provider) => (
                  <option key={provider.code} value={provider.code}>
                    {provider.label}
                  </option>
                ))}
              </NativeSelect>
            )}
          </FormControl>
        )}
      />
      <Controller
        name="modelCode"
        control={control}
        rules={{ required: msg({ id: 'field.model.required' }) }}
        render={({ field }) => (
          <FormControl fullWidth>
            <InputLabel variant="standard" htmlFor="model-native-select">
              {msg({ id: 'field.model.label' })}
            </InputLabel>
            {modelsLoading ? (
              <CircularProgress size={24} />
            ) : (
              <NativeSelect
                {...field}
                label={msg({ id: 'field.model.label' })}
                required
                disabled={!selectedProviderId}
                inputProps={{
                  id: 'model-native-select',
                  name: 'modelCode'
                }}
              >
                <option value="-1">{msg({ id: 'field.model.default.label' })}</option>
                {sortedModels.map((model) => (
                  <option key={model.code} value={model.code}>
                    {model.label}
                  </option>
                ))}
              </NativeSelect>
            )}
          </FormControl>
        )}
      />

      <Box
        sx={{
          borderRadius: 2,
          p: 2,
          backgroundColor: '#fafafa55',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}
      >
        <Stack spacing={0}>
          {items.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'white',
                borderRadius: 1,
                gap: 0,
                m: 0,
                border: '0',
                '&:hover': {
                  backgroundColor: '#f8f9fa',
                  borderColor: '#d0d7de'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <MDInput
                id={`inputClassificationItem${index}`}
                type="text"
                value={item}
                placeholder={`Classification ${index + 1}`}
                onChange={(e) => handleItemChange(index, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddItem();
                  }
                }}
                sx={{
                  flex: 1,
                  p: '5px',
                  margin: 0,
                  border: 'none',
                  outline: 'none',
                  backgroundColor: 'transparent'
                }}
              />

              {items.length > 1 && (
                <Tooltip title={msg({ id: 'tooltip.delete' })}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveItem(index)}
                    sx={{ p: 0.5 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <MDButton
              id="configureBtn"
              size="small"
              color="primary"
              variant="gradient"
              startIcon={<AddIcon />}
              autoFocus
              onClick={handleAddItem}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1
              }}
            >
              {msg({ id: 'button.add' }) || 'Ajouter un élément'}
            </MDButton>
          </Box>
        </Stack>
      </Box>
      <Controller
        sx={{ mb: 2 }}
        name="outputVar"
        control={control}
        render={({ field }) => (
          <MDInput
            {...field}
            label={msg({ id: 'field.outputvar.label' })}
            variant="filled"
            fullWidth
            size="small"
          />
        )}
      />

      <Controller
        name="fileClassification"
        control={control}
        render={({ field: { onChange, value, ...field } }) => (
          <FormControlLabel
            control={<Checkbox onChange={onChange} checked={value} {...field} />}
            label={msg({ id: 'field.classification.file.label' })}
          />
        )}
      />
    </Stack>
  );
}

ClassificationNodeForm.propTypes = {
  id: PropTypes.string,
  data: PropTypes.shape({
    id: PropTypes.number,
    outputVar: PropTypes.string,
    providerCode: PropTypes.string,
    modelCode: PropTypes.string,
    classificationItems: PropTypes.arrayOf(PropTypes.string),
    fileClassification: PropTypes.bool
  }),
  onUpdateData: PropTypes.func.isRequired
};
